import { GraftCodePrimitiveType } from '@graftcode/protocol'

import { UnifiedGraftModelTypeKey } from '@/core/const/unifiedGraftModelTypeKeys.const'
import { getUnifiedGraftModelType } from '@/core/utils/getUnifiedGraftModelType'
import { classifyTypeUsage } from '@/core/utils/mappers/formatTypeUsage/classifyTypeUsage'
import { hasPayload, resolveNode } from '@/core/utils/mappers/formatTypeUsage/nodeHelpers'
import { renderTypeUsage } from '@/core/utils/mappers/formatTypeUsage/renderTypeUsage'
import type { NodeLike, ParameterShape } from '@/core/utils/mappers/formatTypeUsage/types'

import { isGraftableAnyOrObjectType } from './isGraftableAnyOrObjectType'
import { isDownloadableGraftPrimitiveType, isGraftModelReferenceGraftable } from './protocolGraftabilityRules'
import type { GraftabilityAnalysisOptions, GraftabilityIssue, GraftabilityIssueKind } from './types'

function normalizeOptions(options?: string | GraftabilityAnalysisOptions): GraftabilityAnalysisOptions {
  if (typeof options === 'string') {
    return { technology: options }
  }

  return options ?? {}
}

function buildIssue(kind: GraftabilityIssueKind, typeLabel: string, detail: string): GraftabilityIssue {
  return {
    kind,
    typeLabel,
    message: `${detail} Apply a \`--types\` filter when hosting with GG to expose only graft-compatible facades.`,
  }
}

function analyzeShape(
  shape: ParameterShape,
  typeLabel: string,
  options: GraftabilityAnalysisOptions
): GraftabilityIssue[] {
  switch (shape.kind) {
    case 'primitive': {
      const primitiveType = shape.primitiveType
      const typeFqn = shape.typeFqn ?? typeLabel
      if (primitiveType === GraftCodePrimitiveType.GRAFT_MODEL) {
        if (isGraftModelReferenceGraftable(typeFqn, options.hostedTypeFqns)) {
          return []
        }

        return [
          buildIssue(
            'complex-reference',
            typeLabel,
            `Complex reference type \`${typeLabel}\` cannot be included in a downloadable Graft.`
          ),
        ]
      }
      if (!isDownloadableGraftPrimitiveType(primitiveType)) {
        return [
          buildIssue(
            'unsupported-type',
            typeLabel,
            `Type \`${typeLabel}\` is not a supported primitive for downloadable Grafts.`
          ),
        ]
      }
      return []
    }
    case 'array': {
      const elementIssues = analyzeShape(shape.element, typeLabel, options)
      if (elementIssues.length > 0) {
        return [
          buildIssue(
            'complex-array',
            typeLabel,
            `Array type \`${typeLabel}\` contains elements that cannot be grafted.`
          ),
          ...elementIssues,
        ]
      }
      return []
    }
    case 'generic':
      return [
        buildIssue(
          'generic-type',
          typeLabel,
          `Generic type \`${typeLabel}\` cannot be included in a downloadable Graft.`
        ),
        ...shape.args.flatMap((arg, index) =>
          analyzeShape(arg, `${typeLabel}[arg${index}]`, options).map((issue) => ({
            ...issue,
            typeLabel,
          }))
        ),
      ]
    case 'genericAlias':
      return []
    case 'other':
      return [
        buildIssue('unsupported-type', typeLabel, `Type \`${typeLabel}\` is not supported in downloadable Grafts.`),
      ]
    default:
      return []
  }
}

function dedupeIssues(issues: GraftabilityIssue[]): GraftabilityIssue[] {
  const seen = new Set<string>()
  return issues.filter((issue) => {
    const key = `${issue.kind}:${issue.typeLabel}:${issue.message}`
    if (seen.has(key)) return false
    seen.add(key)
    return true
  })
}

export function analyzeTypeUsageGraftability(
  typeUsage: NodeLike | null | undefined,
  options?: string | GraftabilityAnalysisOptions
): { graftable: boolean; issues: GraftabilityIssue[] } {
  const analysisOptions = normalizeOptions(options)

  if (!typeUsage || !hasPayload(typeUsage)) {
    return { graftable: true, issues: [] }
  }

  if (isGraftableAnyOrObjectType(typeUsage)) {
    return { graftable: true, issues: [] }
  }

  const node = resolveNode(typeUsage)
  if (node && getUnifiedGraftModelType(node) === UnifiedGraftModelTypeKey.TYPE_USAGE_VOID) {
    return { graftable: true, issues: [] }
  }

  const typeLabel = renderTypeUsage(typeUsage, analysisOptions.technology) || 'unknown'
  const issues = dedupeIssues(analyzeShape(classifyTypeUsage(typeUsage), typeLabel, analysisOptions))

  return {
    graftable: issues.length === 0,
    issues,
  }
}
